import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api from "@/lib/api";
import VideoEmbed from "@/components/app/VideoEmbed";
import { AlertCircle, ArrowLeft } from "lucide-react";

export default function SymptomLibrary() {
  const [symptoms, setSymptoms] = useState([]);
  useEffect(() => { api.get("/symptoms").then((r) => setSymptoms(r.data)); }, []);

  // narrow_palate is intentionally hidden from the grid — its content lives on the
  // "Palate Size & How to Measure" detail page which is reachable from the merged
  // "Crooked/Crowded Teeth/Narrow Palate" enrollment step (deep-linked to /library/narrow_palate).
  const visible = symptoms.filter((s) => s.key !== "narrow_palate");

  return (
    <Shell>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Learn</div>
        <h1 className="font-display text-3xl font-bold text-navy mt-1">Symptom Library</h1>
        <p className="text-ink-mid mt-2">17 things families ask about. Tap to learn more.</p>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {visible.map((s) => (
          <Link key={s.key} to={`/library/${s.key}`} data-testid={`symptom-card-${s.key}`} className="block bg-warm-white border border-edge rounded-2xl p-5 hover:-translate-y-1 hover:border-gold transition-all">
            <div className="font-display font-bold text-navy">{s.name}</div>
            <p className="mt-2 text-sm text-ink-mid line-clamp-3">{s.description}</p>
            {s.referral && <div className="mt-3 inline-flex items-center gap-1 text-xs text-mauve font-semibold"><AlertCircle size={12} /> Referral note</div>}
          </Link>
        ))}
      </div>
    </Shell>
  );
}

export function SymptomDetail() {
  const { key } = useParams();
  const [s, setS] = useState(null);
  const [walkthroughUrl, setWalkthroughUrl] = useState("");
  useEffect(() => {
    api.get("/symptoms").then((r) => setS(r.data.find((x) => x.key === key)));
    api.get("/content/overrides").then(({ data }) => {
      setWalkthroughUrl(((data.palate_size_measurement || {}).main || {}).video_url || "");
    }).catch(() => {});
  }, [key]);
  if (!s) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;
  const isPalate = s.key === "narrow_palate";
  return (
    <Shell>
      <Link to="/library" className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6"><ArrowLeft size={14} /> All symptoms</Link>
      <div className="max-w-2xl bg-warm-white border border-edge rounded-3xl p-8">
        <h1 className="font-display text-3xl font-bold text-navy">{isPalate ? "Palate Size & How to Measure" : s.name}</h1>
        <p className="mt-4 text-ink-mid leading-relaxed whitespace-pre-line" data-testid={`symptom-description-${s.key}`}>{s.description}</p>
        <div className="mt-6" data-testid={`symptom-video-${s.key}`}>
          <VideoEmbed value={s.video_url} title={isPalate ? "Palate Size — Informational" : s.name} placeholder={isPalate ? "Palate Size — Informational (video coming soon)" : `Video — ${s.name}`} testid={`symptom-video-${s.key}`} />
        </div>
        {isPalate && (
          <div className="mt-6" data-testid="symptom-palate-walkthrough">
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold mb-2">Watch: How to Measure</div>
            <VideoEmbed value={walkthroughUrl} title="Palate Size — Measurement Walkthrough" placeholder="Measurement walkthrough (video coming soon)" testid="symptom-palate-walkthrough-video" />
          </div>
        )}
        {s.safety_note && (
          <div className="mt-6 rounded-2xl bg-cream border border-edge p-5 text-sm text-ink-dark" data-testid={`symptom-safety-${s.key}`}>
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold mb-1">Safety note</div>
            {s.safety_note}
          </div>
        )}
        {s.referral && (
          <div className="mt-6 rounded-2xl bg-mauve-pale border border-mauve p-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-mauve mt-0.5" size={20} />
              <div>
                <div className="font-semibold text-navy">Talk to your provider if…</div>
                <div className="text-sm text-ink-dark mt-1">{s.referral}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
